/**
 * feat-107 shared primitive (1/4) — Curriculum display labels.
 *
 * AUTHORITY: feat-198 ADR 0001 (`decisions/0001-learn-terminology-rename-alias-
 * contract-and-route-map.md`). It supersedes ADR 0022 **§D1 only** — §D2
 * (`IconPickerPopover`), §D3 (commit-orchestration helper) and §D4 (parser
 * module interface) survive intact, so anyone citing those need do nothing.
 *
 * WHAT CHANGED, AND WHY THIS HEADER NO LONGER MAPS NAMES. Until feat-198 this
 * file claimed to be the single source of truth for a *display-only* rename,
 * and carried a DB/API mapping table asserting those names were unchanged.
 * feat-198 renamed the tables, the columns and the API paths, so the premise is
 * gone: the display name IS the DB name now, and this file's remaining job is
 * plural / badge / count helpers (`termCount`, `crumb`, `*_BADGE`) — nothing
 * else. The mapping claims are deliberately NOT restated here. Two of them had
 * never been checked against the schema (the level-3 rehearsal key named a table
 * that does not exist), and a second copy of a name is a second thing to drift.
 * The `entity_type` literals the API actually keys on live in ONE place:
 * `CURRICULUM_ENTITY_TYPE` in `src/services/quizContentAdminService.ts`.
 *
 * "Drill" is the product's word for level 2 — human decision #1 / #5 in the
 * feat-107 spec; the mockups said "Topic", which the human OVERRODE.
 *
 * Product hierarchy:  Lesson  ›  Drill  ›  Question / Rehearsal / Video / Reading
 *
 * Every label, breadcrumb crumb-prefix, button, empty-state line, and importer
 * string in BOTH slices reads from here. Do not hard-code "Lesson"/"Drill"/etc.
 * anywhere in the editor or importer — import these instead so the rename can
 * never drift.
 */

export interface TermLabel {
  /** e.g. "Drill" */
  readonly singular: string;
  /** e.g. "Drills" */
  readonly plural: string;
}

/**
 * The hierarchy levels, by their product (display) name.
 *
 * The per-key DB/API mappings that used to sit on these lines were struck by
 * feat-198 along with the header's — see above for why they are not restated.
 * Level numbers stay because they are what a caller picking a key needs.
 */
export const QUIZ_TERMS = {
  /** lvl 0 — feat-235 curriculum track. */
  track: { singular: 'Track', plural: 'Tracks' },
  /** lvl 1. */
  lesson: { singular: 'Lesson', plural: 'Lessons' },
  /** lvl 2. */
  drill: { singular: 'Drill', plural: 'Drills' },
  /** lvl 3. */
  question: { singular: 'Question', plural: 'Questions' },
  /** lvl 3. */
  rehearsal: { singular: 'Rehearsal', plural: 'Rehearsals' },
  /** lvl 3 — feat-165 explanatory video item. */
  video: { singular: 'Video', plural: 'Videos' },
  /** lvl 3 — feat-166 reading item. */
  reading: { singular: 'Reading', plural: 'Readings' },
} as const satisfies Record<string, TermLabel>;

export type QuizTermKey = keyof typeof QUIZ_TERMS;

/**
 * Short type badges used on list rows / inferred-item cards / progress lines
 * (mockups: `MC` / `Fill` / `Rehearsal`). Question badges are keyed by the
 * live `QuestionType` discriminant so call sites can index directly.
 */
export const QUESTION_TYPE_BADGE = {
  multiple_choice: 'MC',
  fill_blank: 'Fill',
} as const;

export const REHEARSAL_BADGE = QUIZ_TERMS.rehearsal.singular; // "Rehearsal"

export const VIDEO_BADGE = QUIZ_TERMS.video.singular; // "Video" (feat-165)

export const READING_BADGE = QUIZ_TERMS.reading.singular; // "Reading" (feat-166)

/** Count helper: `count(2, 'drill') → "2 Drills"`, `count(1, 'lesson') → "1 Lesson"`. */
export function termCount(count: number, key: QuizTermKey): string {
  const t = QUIZ_TERMS[key];
  return `${count} ${count === 1 ? t.singular : t.plural}`;
}

/**
 * Breadcrumb crumb with its level word prefixed, e.g. `Lesson Upsell Mastery`.
 * The mockup renders the level word small/muted before the name — slices style
 * it; this returns the two parts so styling stays in the component.
 */
export function crumb(key: QuizTermKey, name: string): { level: string; name: string } {
  return { level: QUIZ_TERMS[key].singular, name };
}
